// Updated TenantContext.js with RLS-safe queries
import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'

const TenantContext = createContext()

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

export const TenantProvider = ({ children }) => {
  const { user, initialized } = useAuth()
  const [tenants, setTenants] = useState([])
  const [currentTenant, setCurrentTenant] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Use refs to prevent infinite loops
  const tenantsFetchedRef = useRef(false)
  const currentUserIdRef = useRef(null)

  useEffect(() => {
    let mounted = true

    console.log('TenantContext useEffect:', { 
      user: user?.email, 
      initialized, 
      tenantsFetched: tenantsFetchedRef.current,
      currentUserId: currentUserIdRef.current,
      newUserId: user?.id
    })
    
    if (user && initialized) {
      // Only fetch if user changed or we haven't fetched yet
      if (currentUserIdRef.current !== user.id || !tenantsFetchedRef.current) {
        currentUserIdRef.current = user.id
        tenantsFetchedRef.current = true
        fetchUserTenants()
      }
    } else if (!user && initialized) {
      // User logged out, reset state
      console.log('User logged out, resetting tenant state')
      if (mounted) {
        setTenants([])
        setCurrentTenant(null)
        setUserRole(null)
        setLoading(false)
        setError(null)
        tenantsFetchedRef.current = false
        currentUserIdRef.current = null
      }
    } else if (initialized) {
      // No user and initialized
      setLoading(false)
    }

    return () => {
      mounted = false
    }
  }, [user?.id, initialized]) // Only depend on user ID and initialized flag

  const fetchUserTenants = async () => {
    try {
      console.log('Fetching tenants for user:', user.id)
      setLoading(true)
      setError(null)
      
      // Check for pending invitations first and auto-accept them
      console.log('Checking for pending invitations for:', user.email)
      const pendingResult = await supabase.rpc('check_pending_invitations', {
        p_user_id: user.id,
        p_email: user.email
      })
      
      if (pendingResult.data?.count > 0) {
        console.log('Auto-accepted pending invitations:', pendingResult.data.accepted_invitations)
      }
      
      // Use the new RLS-safe function instead of direct table queries
      console.log('Fetching tenants via RLS-safe function...')
      const { data, error: fetchError } = await supabase.rpc('get_user_tenants', {
        user_uuid: user.id
      })

      if (fetchError) {
        console.error('Error fetching tenants via function:', fetchError)
        // Fallback to the old method if the function doesn't exist yet
        return await fetchTenantsOldMethod()
      }

      console.log('Tenant data from function:', data?.length || 0)

      // Transform the data to match expected format
      const userTenants = data?.map(tenant => ({
        ...tenant,
        // The function should return role info, but if not, we'll need to fetch it
        role: tenant.role || 'user', // Default role
        membershipId: tenant.membership_id
      })) || []

      setTenants(userTenants)
      
      // Set current tenant logic
      if (userTenants.length > 0) {
        console.log('User has tenants:', userTenants.length)
        
        // Try to load previously selected tenant
        const savedTenantId = localStorage.getItem('currentTenantId')
        const savedTenant = userTenants.find(t => t.id === savedTenantId)
        
        if (savedTenant) {
          console.log('Switching to saved tenant:', savedTenant.name)
          switchTenant(savedTenant)
        } else {
          console.log('Switching to first tenant:', userTenants[0].name)
          switchTenant(userTenants[0])
        }
      } else {
        console.log('User has no tenants')
        setCurrentTenant(null)
        setUserRole(null)
        setError(null)
      }
    } catch (err) {
      console.error('Error fetching tenants:', err)
      setError('Failed to load organizations: ' + err.message)
      setCurrentTenant(null)
      setUserRole(null)
    } finally {
      setLoading(false)
    }
  }

  // Fallback method for when RLS function isn't available yet
  const fetchTenantsOldMethod = async () => {
    console.log('Using fallback method for tenant fetching...')
    
    // Try without the join first to avoid RLS recursion
    const { data: memberships, error: membershipError } = await supabase
      .from('tenant_memberships')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'active')

    if (membershipError) throw membershipError

    console.log('Found memberships:', memberships?.length || 0)

    if (!memberships || memberships.length === 0) {
      return []
    }

    // Then fetch tenants separately to avoid RLS issues
    const tenantIds = memberships.map(m => m.tenant_id)
    const { data: tenants, error: tenantsError } = await supabase
      .from('tenants')
      .select('*')
      .in('id', tenantIds)

    if (tenantsError) throw tenantsError

    // Combine the data manually
    const combinedData = memberships.map(membership => ({
      ...tenants.find(t => t.id === membership.tenant_id),
      role: membership.role,
      membershipId: membership.id
    }))

    console.log('Combined tenant data:', combinedData?.length || 0)

    setTenants(combinedData)
    
    // Set current tenant logic (same as above)
    if (combinedData.length > 0) {
      const savedTenantId = localStorage.getItem('currentTenantId')
      const savedTenant = combinedData.find(t => t.id === savedTenantId)
      
      if (savedTenant) {
        switchTenant(savedTenant)
      } else {
        switchTenant(combinedData[0])
      }
    }
  }

  const switchTenant = (tenant) => {
    console.log('Switching to tenant:', tenant.name, 'role:', tenant.role)
    setCurrentTenant(tenant)
    setUserRole(tenant.role)
    localStorage.setItem('currentTenantId', tenant.id)
  }

  const createTenantForUser = async (tenantName) => {
    try {
      console.log('Creating tenant:', tenantName, 'for user:', user.id)
      
      const { data, error } = await supabase.rpc('create_tenant_for_user', {
        p_user_id: user.id,
        p_tenant_name: tenantName,
        p_user_email: user.email,
        p_user_full_name: user.user_metadata?.full_name || user.email
      })

      if (error) {
        console.error('Error creating tenant:', error)
        throw error
      }

      console.log('Tenant created successfully:', data)

      // Reset fetch flag and refresh tenants
      tenantsFetchedRef.current = false
      await fetchUserTenants()
      
      return { success: true, tenantId: data }
    } catch (error) {
      console.error('Error creating tenant:', error)
      return { success: false, error: error.message }
    }
  }

  const refetchTenants = async () => {
    tenantsFetchedRef.current = false
    await fetchUserTenants()
  }

  const value = {
    tenants,
    currentTenant,
    userRole,
    loading,
    error,
    switchTenant,
    createTenantForUser,
    refetchTenants,
  }

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}